/**
 * Editor island port (#239): the only surface the shell (EditorShell,
 * stores, chrome) may use to talk to the manuscript editor. Everything else
 * under `src/editor/**` is island-internal.
 *
 * The editor is ProseMirror over `.zwdoc` (`src/editor/prose/`), hosted by
 * `DocumentEditor.vue`. Keep this file free of editor-library types.
 *
 * Inbound state does not go through the port: the editor reads the active
 * document and display state from Pinia (`useEditorSessionStore` content /
 * paths, `useEditorDisplayStore` mode + typewriter, theme, project dictionary).
 * Saves go out through `useEditorSaveStore`. The port covers imperative
 * commands and the events the editor raises.
 *
 * Known leaks:
 * - `scrollToHeading(from)` takes a plain-text projection offset from
 *   occurrences / project search (`lib/zwdoc/scanText.ts`).
 * - `showTrashPreview` takes raw file text; the editor renders it read-only
 *   (a pre-conversion Markdown file is converted for display, #252).
 * - Island-internal composables (`useFormatBubble`, `useHistoryReview`,
 *   `useImageToolbar`, `useEntityPeek`, `useCommentThreads`,
 *   `useEditorLayout`) live under `src/composables/`; the comment ones take a
 *   `CommentSurface` (`commentSurface.ts`).
 */

/** Ribbon / bubble / menu formatting commands. */
export type FormatCommand =
  | "undo"
  | "redo"
  | "bold"
  | "italic"
  | "underline"
  | "strikethrough"
  | "highlight"
  | "link"
  | "image"
  | "spacer"
  | "bulletList"
  | "numberedList"
  | "quote"
  | "indent"
  | "outdent"
  | "alignLeft"
  | "alignCenter"
  | "alignRight"
  | "heading1"
  | "heading2"
  | "heading3"
  | "heading4"
  | "heading5"
  | "heading6"
  | "addComment";

/** Commands the shell may send. Exposed by `DocumentEditor.vue`. */
export type EditorPort = {
  focus(): void;
  runCommand(command: FormatCommand): void;
  scrollToComment(id: string, focusLine?: boolean): void;
  /** Plain-text offset from `useEditorHeadingsStore` (see known leaks). */
  scrollToHeading(from: number): void;
  toggleHistory(): void;
  toggleComments(): void;
  closeHistory(): void;
  openFind(): void;
  findNext(): void;
  selectNextOccurrence(): void;
  openThesaurus(): void;
  ensureCaretVisible(behavior?: ScrollBehavior): void;
  toggleOutline(): void;
  /** Read-only preview of a deleted file's raw text. */
  showTrashPreview(content: string, label: string, path: string): void;
  clearTrashPreview(restoreLiveDoc?: boolean): void;
};

/**
 * The routed main view may be the editor or another page (graph, database
 * list), so shell holders of a template ref see every command as optional.
 */
export type EditorPortHandle = Partial<EditorPort>;

/** Events the editor emits to its parent (Vue `defineEmits` shape). */
export type EditorPortEvents = {
  selectComment: [id: string];
  commentAdded: [id: string];
  formatCommand: [command: FormatCommand];
};

/** A range of document text the shell captured earlier, with its contents. */
export type EditorTextRange = {
  from: number;
  to: number;
  /** Text expected at `from..to`; a mismatch means the document moved on. */
  expected: string;
};

/**
 * Text replacement for shell features that act on a word the user picked in
 * the editor (thesaurus). Registered by the editor with `useWordLookupStore`.
 */
export type EditorTextPort = {
  /**
   * Replace `range` with `text`, put the caret after it and focus the editor.
   * Returns false (and changes nothing) when the editor is read-only, the
   * range is out of bounds, or the text there no longer matches.
   */
  replaceExpectedRange(range: EditorTextRange, text: string): boolean;
};

/** Viewport coordinates of a document position (`coordsAtPos`). */
export type EditorCaretRect = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

/**
 * Selection facts shell chrome (format bubble) needs, independent of the
 * editor library. Positions are editor-defined; only compare them to each other.
 */
export type EditorSelectionSnapshot = {
  empty: boolean;
  from: number;
  to: number;
  hasFocus: boolean;
  coordsAtPos(pos: number): EditorCaretRect | null;
};

/**
 * Chapter opening for the open document (Styling ▸ Paragraphs): set by the
 * shell only when the document is a chapter's first scene. The editor marks
 * the initial / lead-in; the shell's data attributes choose how they look.
 */
export type EditorChapterOpening = {
  readonly initial: boolean;
  readonly leadIn: "none" | "words" | "sentence";
};

export const NO_EDITOR_CHAPTER_OPENING: EditorChapterOpening = {
  initial: false,
  leadIn: "none",
};

/** An image in the document, as the image toolbar sees it (positions are editor-defined). */
export type EditorImage = {
  from: number;
  to: number;
  alt: string;
  src: string;
};
